import { useMemo } from "react";
import { MoreHorizontal, Shirt } from "lucide-react";
import { Button } from "../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Card, CardContent } from "../../ui/card";
import { DataTable } from "../../ui/data-table/data-table";
import { DataTableColumnHeader } from "../../ui/data-table/data-table-column-header";
import { ExportMenu } from "../../ui/export-menu";
import { SummaryHeader } from "../../ui/SummaryHeader";
import SalesLoader from "../../../components/loaders/SalesLoader";
import { useEcommTopSelling } from "../../e-commerce/hooks/useEcommTopSelling";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatNumber } from "../../../utils/formatNumber";

interface TopProductsProps {
  selectedMonth: string;
}

export function RetailTopProducts({ selectedMonth }: TopProductsProps)  {
  

  const topSellingParams = {
    month: selectedMonth,
    limit: 20,
  };

  const { data: topSellingData, isLoading } = useEcommTopSelling(topSellingParams);

  const products = useMemo(() => {
    return (topSellingData || []).map((product, index) => ({
      id: index,
      sku: product.sku,
      name: product.title,
      category: "", // Not available in API
      price: product.avg_price,
      cost: 0, // Placeholder
      stockLevel: product.total_quantity,
      reorderPoint: 10,
      sales: product.sale_count,
      revenue: product.total_revenue,
      profit: product.total_revenue - 0,
    }));
  }, [topSellingData]);



  const columns = [
    {
      accessorKey: "name",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Product" />,
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-zinc-100 flex items-center justify-center">
            <Shirt className="h-4 w-4 text-zinc-500" />
          </div>
          <div>
            <div className="font-medium">{row.getValue("name")}</div>
            <div className="text-xs text-muted-foreground">{row.original.sku}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "sku",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="SKU" />,
    },
    {
      accessorKey: "price",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Price" />,
      cell: ({ row }: any) => formatCurrency(row.getValue("price")),
    },
    {
      accessorKey: "sales",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Sales" />,
      cell: ({ row }: any) => formatNumber(row.getValue("sales")),
    },
    {
      accessorKey: "revenue",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Revenue" />,
      cell: ({ row }: any) => formatCurrency(row.getValue("revenue")),
    },
    {
      accessorKey: "profit",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Profit" />,
      cell: ({ row }: any) => formatCurrency(row.getValue("profit")),
    },
    {
      accessorKey: "stockLevel",
      header: ({ column }: any) => <DataTableColumnHeader column={column} title="Stock" />,
      cell: ({ row }: any) => {
        const stockLevel = row.getValue("stockLevel");
        const reorderPoint = row.original.reorderPoint;
        return (
          <span className={stockLevel < reorderPoint ? "text-red-500" : ""}>
            {formatNumber(stockLevel)}
          </span>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }: any) => {
        const product = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(product.sku)}
              >
                Copy SKU
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <SummaryHeader title="Top Products" />
      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4">
          <div className="w-full flex justify-end gap-2">
            <ExportMenu
              data={products}
              filename="top_products"
              buttonSize="sm"
              label=""
            />
          </div>
        </div>
        {isLoading ? (
          <SalesLoader variant="top-product" />
        ) : (
          <CardContent className="p-4">
            <DataTable
              columns={columns}
              data={products}
              searchColumn="name"
              searchPlaceholder="Search Product..."
              initialPageSize={10}
            />
          </CardContent>
        )}
      </Card>
    </>
  );
}
